import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaCode,
  FaCog,
  FaCompass,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaLayerGroup,
  FaPlus,
  FaRoute,
  FaSearch,
  FaTrash,
  FaUsers
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';

const STATUS_LABELS = {
  0: { label: 'Inactive', bg: 'secondary' },
  1: { label: 'Active', bg: 'success' },
  2: { label: 'Draft', bg: 'warning' }
};

const PAGE_SIZE = 10;

const JourneyActions = ({ journey, onNavigate, onDelete }) => {
  const actions = [
    {
      label: 'Edit',
      icon: FaEdit,
      path: 'edit',
      className: 'border-purple-300 text-purple-600 hover:bg-purple-100'
    },
    {
      label: 'Builder',
      icon: FaLayerGroup,
      path: 'builder',
      className: 'border-gray-300 text-gray-600 hover:bg-gray-100'
    },
    {
      label: 'Enrollments',
      icon: FaUsers,
      path: 'enrollments',
      className: 'border-blue-300 text-blue-600 hover:bg-blue-50'
    },
    {
      label: 'Reports',
      icon: FaChartBar,
      path: 'reports',
      className: 'border-cyan-300 text-cyan-600 hover:bg-cyan-50'
    }
  ];

  return (
    <div className="flex items-center justify-start gap-1.5">
      {actions.map(({ label, icon: Icon, path, className }) => (
        <Button
          key={path}
          bsPrefix="custom-hamburger"
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white p-0 ${className}`}
          onClick={() => onNavigate(journey.id, path)}
          aria-label={`${label} ${journey.name}`}
          title={label}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      ))}
      <Button
        bsPrefix="custom-hamburger"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 bg-white p-0 text-red-600 hover:bg-red-50"
        onClick={() => onDelete(journey)}
        aria-label={`Delete ${journey.name}`}
        title="Delete"
      >
        <FaTrash className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
};

const LearningJourneyList = () => {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_SIZE,
    pages: 1
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadJourneys = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      const res = await api.learningJourneys.getAll({
        search: debouncedSearchTerm || undefined,
        status: statusFilter !== '' ? statusFilter : undefined,
        page: currentPage,
        limit: PAGE_SIZE
      });

      if (requestId !== requestIdRef.current) return;

      setJourneys(res.data?.data || []);
      setPagination(res.data?.pagination || {
        total: res.data?.data?.length || 0,
        page: currentPage,
        limit: PAGE_SIZE,
        pages: 1
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      toast.error(error.response?.data?.message || 'Failed to load learning journeys');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, statusFilter]);

  useEffect(() => {
    loadJourneys();
  }, [loadJourneys]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleNavigate = (journeyId, destination) => {
    navigate(`/admin/learning-journeys/${journeyId}/${destination}`);
  };

  const handleOpenDeleteModal = (journey) => {
    setSelectedJourney(journey);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setSelectedJourney(null);
  };

  const handleDelete = async () => {
    if (!selectedJourney) return;

    try {
      setDeleting(true);
      await api.learningJourneys.delete(selectedJourney.id);
      toast.success('Learning journey deleted');
      setShowDeleteModal(false);
      setSelectedJourney(null);

      if (journeys.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        loadJourneys();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete journey');
    } finally {
      setDeleting(false);
    }
  };

  const firstResult = pagination.total === 0
    ? 0
    : (pagination.page - 1) * pagination.limit + 1;
  const lastResult = Math.min(pagination.page * pagination.limit, pagination.total);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-96 p-4">
        <Spinner animation="border" role="status" className="text-purple-600 w-12 h-12">
          <span className="visually-hidden">Loading learning journeys...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Learning Journeys
        </h5>
      </div>

      <Row className="mb-3 g-2 sm:g-3">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-4 h-4" aria-hidden="true" />
            </InputGroup.Text>
            <Form.Control
                type="search"
                placeholder="Search by name, code, or category..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                aria-label="Search learning journeys"
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select
            value={statusFilter}
            onChange={handleStatusChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
            aria-label="Filter learning journeys by status"
          >
            <option value="">All Status</option>
            <option value="2">Draft</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => navigate('/admin/learning-journeys/create')}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" aria-hidden="true" />
            <span className="whitespace-nowrap">Add Learning Journey</span>
          </Button>
        </Col>
      </Row>

      {journeys.length === 0 ? (
        <Alert variant="info" className="text-center py-6 sm:py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="mb-0 text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || statusFilter
              ? 'No learning journeys found matching your criteria.'
              : 'No learning journeys found. Click "Add Learning Journey" to create the first journey.'}
          </p>
        </Alert>
      ) : (
        <>
          <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
            <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  <th scope="col" className="!bg-purple-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaRoute aria-hidden="true" /> Journey</span>
                  </th>
                  <th scope="col" className="!bg-purple-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaCode aria-hidden="true" /> Code</span>
                  </th>
                  <th scope="col" className="!bg-purple-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaClock aria-hidden="true" /> Duration</span>
                  </th>
                  <th scope="col" className="!bg-purple-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaCompass aria-hidden="true" /> Navigation</span>
                  </th>
                  <th scope="col" className="!bg-purple-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaCheckCircle aria-hidden="true" /> Status</span>
                  </th>
                  <th scope="col" className="!bg-pink-600 !px-3 !py-3 !text-white">
                    <span className="flex items-center gap-2"><FaCog aria-hidden="true" /> Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {journeys.map((journey) => {
                  const status = STATUS_LABELS[journey.status] || STATUS_LABELS[2];
                  return (
                    <tr key={journey.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                      <td className="py-2 px-3">
                        <div className="d-flex align-items-center gap-2">
                          {journey.thumbnail ? (
                            <img
                              src={getAssetUrl(journey.thumbnail)}
                              alt={journey.name}
                              className="w-10 h-10 rounded-lg object-cover shadow-sm"
                            />
                          ) : (
                            <span className="flex w-10 h-10 flex-none items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                              <FaRoute aria-hidden="true" />
                            </span>
                          )}
                          <div>
                            <div className="fw-semibold text-gray-800 text-xs sm:text-sm">{journey.name}</div>
                            <small className="text-muted text-xs">{journey.category || 'Uncategorized'}</small>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-purple-700 font-medium">{journey.code}</td>
                      <td className="py-2 px-3 text-gray-600">{journey.estimated_duration || '-'}</td>
                      <td className="py-2 px-3 text-gray-600">
                        {journey.navigation_mode === 'free' ? 'Free navigation' : 'Sequential'}
                      </td>
                      <td className="py-2 px-3">
                        <Badge bg={status.bg} className="px-2 py-1 text-xs">{status.label}</Badge>
                      </td>
                      <td className="py-2 px-3">
                        <JourneyActions
                          journey={journey}
                          onNavigate={handleNavigate}
                          onDelete={handleOpenDeleteModal}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2 mt-3">
            <p className="mb-0 text-sm text-gray-600" aria-live="polite">
              Showing {firstResult}–{lastResult} of {pagination.total}
            </p>
            <div className="d-flex align-items-center gap-2">
              <Button
                bsPrefix="custom-hamburger"
                className="border-gray-300 bg-white text-gray-700 rounded-lg px-3 py-2 text-xs sm:text-sm disabled:opacity-50"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={pagination.page <= 1}
                aria-label="Go to previous page"
              >
                <FaChevronLeft aria-hidden="true" />
              </Button>
              <span className="px-2 text-xs sm:text-sm text-gray-600">
                Page {pagination.page} of {Math.max(1, pagination.pages)}
              </span>
              <Button
                bsPrefix="custom-hamburger"
                className="border-gray-300 bg-white text-gray-700 rounded-lg px-3 py-2 text-xs sm:text-sm disabled:opacity-50"
                onClick={() => setCurrentPage((page) => Math.min(pagination.pages, page + 1))}
                disabled={pagination.page >= pagination.pages}
                aria-label="Go to next page"
              >
                <FaChevronRight aria-hidden="true" />
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered fullscreen="sm-down">
        <Modal.Header
          closeButton={!deleting}
          closeVariant="white"
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header"
        >
          <Modal.Title className="text-sm sm:text-base">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-3 flex justify-center items-center">
            <FaRoute size={50} className="text-gray-400" aria-hidden="true" />
          </div>
          <h5 className="font-bold text-gray-800 text-sm sm:text-base">Delete Learning Journey</h5>
          <p className="text-muted text-xs sm:text-sm">
            Are you sure you want to delete <strong>{selectedJourney?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDelete}
                disabled={deleting}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleting ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Journey'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LearningJourneyList;
